import { types, flow } from "mobx-state-tree";
import Persistence from "pages/common/lib/grabyo-persistence";
import * as CredentialsAPI from "api/socialcredentials";

import BaseBackboneCollectionMigrationStore from "../backbone-collection-template";

import { UserCredentialBaseStore } from "./user-credential-base-store";
import { UserCredentialFacebookStore } from "./user-credential-facebook-store";
import { UserCredentialBrightcoveStore } from "./user-credential-brightcove-store";
import { UserCredentialFTPStore } from "./user-credential-ftp-store";
import { UserCredentialInstagramStore } from "./user-credential-instagram-store";
import { UserCredentialKhorosStore } from "./user-credential-khoros-store";
import { UserCredentialOptaStore } from "./user-credential-opta-store";
import { UserCredentialS3Store } from "./user-credential-s3-store";
import { UserCredentialThePlatformStore } from "./user-credential-theplatform-store";
import { UserCredentialTikTokStore } from "./user-credential-tiktok-store";
import { UserCredentialTwitchStore } from "./user-credential-twitch-store";
import { UserCredentialTwitterStore } from "./user-credential-twitter-store";
import { UserCredentialYoutubeStore } from "./user-credential-youtube-store";

const dispatchCredentialType = {
    dispatcher: (snapshot) => {
        switch (snapshot.credentialType) {
            case `facebook`:
            case `facebook_page`:
                return UserCredentialFacebookStore;
            case `brightcove`:
                return UserCredentialBrightcoveStore;
            case `twitter`:
                return UserCredentialTwitterStore;
            case `youtube`:
                return UserCredentialYoutubeStore;
            case `spredfast`:
            case `khoros`:
                return UserCredentialKhorosStore;
            case `theplatform`:
                return UserCredentialThePlatformStore;
            case `instagram`:
                return UserCredentialInstagramStore;
            case `opta`:
                return UserCredentialOptaStore;
            case `ftp`:
                return UserCredentialFTPStore;
            case `s3`:
                return UserCredentialS3Store;
            case `twitch`:
                return UserCredentialTwitchStore;
            case `tiktok`:
                return UserCredentialTikTokStore;
            default:
                return UserCredentialBaseStore;
        }
    }
};

const credentialTypesUnion = types.union(
    dispatchCredentialType,
    UserCredentialFacebookStore,
    UserCredentialBrightcoveStore,
    UserCredentialFTPStore,
    UserCredentialInstagramStore,
    UserCredentialKhorosStore,
    UserCredentialOptaStore,
    UserCredentialS3Store,
    UserCredentialThePlatformStore,
    UserCredentialTikTokStore,
    UserCredentialTwitchStore,
    UserCredentialTwitterStore,
    UserCredentialYoutubeStore,
    UserCredentialBaseStore
);

const model = {
    socialCredentials: types.optional(
        types.array(types.late(() => credentialTypesUnion)),
        []
    )
};

const preProcessor = (snapshot) => {
    if (Array.isArray(snapshot)) {
        return {
            socialCredentials: [...snapshot]
        };
    }
    return snapshot;
};

const volatile = () => ({});

const views = (self) => ({
    get length() {
        return self.socialCredentials.length;
    },

    getUserCredentialById(socialId) {
        return self.socialCredentials.find(
            (credential) => credential.socialId === socialId
        );
    },

    isTypeEnabled(type) {
        return self.socialCredentials.filter((credential) => {
            return (
                credential.type === type &&
                credential.enabled &&
                credential.isActive()
            );
        });
    },

    hasType(type) {
        return self.socialCredentials.some((credential) => {
            return credential.type === type && credential.isActive();
        });
    },

    getEnabledCredentials() {
        return self.socialCredentials.filter(
            (credential) => credential.enabled && credential.isActive()
        );
    },

    getEnabledCredentialsForType(type, options = {}) {
        const activeCredentials = self.isTypeEnabled(type);
        if (options.rawClip) {
            const allowedTypes = [`facebook`, `youtube`, `twitter`];
            return activeCredentials.filter((credential) => {
                return allowedTypes.includes(credential.type);
            });
        }
        return activeCredentials;
    },

    countEnabledCredentials() {
        const socialCredentialsCount = self.getEnabledCredentials().length;
        const galleryEnabled = Persistence.PermStore.get(`galleryEnabled`);
        return socialCredentialsCount + (galleryEnabled === true ? 1 : 0);
    },

    /**
     * @summary This counts the number of credential activated on the user
     * But only for the social platform that requires a social message
     */
    countEnabledCredentialsForSocialMessage() {
        const enabledCredentials = self.getEnabledCredentials();
        return enabledCredentials.filter((credential) => {
            return (
                credential.type === `twitter` || credential.type === `facebook`
            );
        }).length;
    },

    getActiveSocialCredentialsForClipSharing() {
        return self.socialCredentials.filter((credential) => {
            return (
                credential.isClipSharingEnabledForType() &&
                !credential.isDeprecated() &&
                (credential.isActive() || credential.isInvalid())
            );
        });
    },

    getEnabledSocialCredentialsForClipSharing() {
        return self.socialCredentials.filter(
            (credential) =>
                credential.isActive() &&
                !credential.isDeprecated() &&
                credential.enabled &&
                credential.isClipSharingEnabledForType()
        );
    },

    getAllSocialIdsForEnabledCredentialsForClipSharing() {
        const enabledCreds = self.getEnabledSocialCredentialsForClipSharing();
        return enabledCreds.map((credential) => credential.socialId);
    },

    getActiveSocialCredentialsForLiveStreaming() {
        return self.socialCredentials.filter((credential) => {
            return (
                credential.isLiveStreamingEnabledForType() &&
                (credential.isActive() || credential.isInvalid())
            );
        });
    },

    getCredentialsByType(type) {
        return self.socialCredentials.filter(
            (credential) => credential.type === type
        );
    },

    getActiveSocialCredentials() {
        return self.socialCredentials.filter((credential) =>
            credential.isActive()
        );
    },

    // This method assigns exportedEncodeRecord backbone models
    // to user credential stores. The exportedEncodeRecords should be
    // migrated ASAP
    updateExportedEncodeRecords(encodeRecords) {
        encodeRecords.forEach((record) => {
            const credentialId = record.get(`userSocialCredentialId`);
            const credential = self.getUserCredentialById(credentialId);
            if (credential) {
                credential.setExportedEncodeRecord(record);
            }
        });
    },

    /**
     * This returns the smallest maximum duration for a given social credential type
     * It is needed for Twitter Native that can accept clips that are 2mn20 or 10mn
     * @param  {String} type [social credential type we target]
     * @return {Number}      [duration in ms]
     */
    getClipMaxDuration(type) {
        return Math.min.apply(
            self,
            self
                .isTypeEnabled(type)
                .map((credential) => credential.getClipMaxDuration())
        );
    }
});

const actions = (self) => ({
    toggleEnabledSocialCredentialsForClipSharing(socialIds) {
        socialIds.forEach((socialId) => {
            const credential = self.socialCredentials.find(
                (_credential) => _credential.socialId === socialId
            );
            credential?.setEnabled(false);
        });
    }
});

const asyncActions = (self) => ({
    /**
     * @summary Make an api call to fetch all the current users credentials
     * @async
     * @return {Credentials | Error}
     */
    fetch: flow(function* fetch() {
        try {
            const data = yield CredentialsAPI.getUserCredentials();
            self.socialCredentials = data;
            return self.socialCredentials;
        } catch (error) {
            console.error(error);
            return error;
        }
    }),

    /**
     * @summary Toggle (deselect) multiple credentials
     * @async
     */
    toggleSocialCredentialsForClipSharing: flow(
        function* toggleSocialCredentialsForClipSharing() {
            const socialIds =
                self.getAllSocialIdsForEnabledCredentialsForClipSharing();

            try {
                yield CredentialsAPI.toggleMultipleCredentialsStatus(
                    socialIds.join(`,`)
                );
            } catch (error) {
                console.error(error);
                App.Events.trigger(
                    `fail:toggleStatusMultipleSocialAccounts`,
                    socialIds
                );
                return error;
            }
        }
    )
});

const _UserCredentialsStore = types
    .model(`Credentials`, model)
    .preProcessSnapshot(preProcessor)
    .volatile(volatile)
    .views(views)
    .actions(actions)
    .actions(asyncActions);

export const UserCredentialsStore = BaseBackboneCollectionMigrationStore(
    _UserCredentialsStore,
    UserCredentialBaseStore,
    `socialCredentials`
);
